import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Navigation from "../components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  TrendingUp, 
  Activity, 
  Brain, 
  Target, 
  Lightbulb, 
  AlertCircle,
  CheckCircle,
  ArrowRight,
  Calendar,
  BarChart3,
  Sparkles
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PerformanceTrends({ user, onLogout }) {
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTrends();
  }, []);

  const fetchTrends = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/journal/trends`, { headers });
      
      if (response.data.success) {
        setTrends(response.data);
      } else {
        setError(response.data.message || "Unable to generate trends");
      }
    } catch (error) {
      console.error("Failed to fetch trends:", error);
      setError(error.response?.data?.detail || "Failed to load performance trends");
      toast.error("Failed to load trends");
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'physical': return <Activity className="w-4 h-4" />;
      case 'mental': return <Brain className="w-4 h-4" />;
      case 'performance': return <Target className="w-4 h-4" />;
      default: return <Lightbulb className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        <Navigation user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
            <h3 className="text-xl font-semibold text-gray-700">Analyzing Your Performance Data...</h3>
            <p className="text-gray-600 mt-2">Our AI is identifying patterns and insights</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        <Navigation user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Card className="border-0 shadow-lg text-center py-12">
            <CardContent>
              <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">{error}</h3>
              <p className="text-gray-600 mb-6">
                {trends?.entries_count !== undefined 
                  ? `You have ${trends.entries_count} entries. Keep journaling to unlock AI-powered insights!`
                  : "Start tracking your performance to see personalized trends and insights."}
              </p>
              <Button 
                onClick={() => navigate('/journal')} 
                className="bg-purple-600 hover:bg-purple-700"
              >
                Go to Performance Journal
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const trendsData = trends?.trends;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-10 h-10 text-purple-600" />
              <div>
                <h1 className="text-4xl font-bold text-gray-900">Performance Trends</h1>
                <p className="text-gray-600">AI-powered insights from your journal entries</p>
              </div>
            </div>
            <Button 
              onClick={fetchTrends} 
              variant="outline"
              className="border-purple-600 text-purple-600 hover:bg-purple-50"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Refresh Analysis
            </Button>
          </div>

          {/* Date Range Info */}
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <Badge variant="outline" className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {trends?.date_range?.from} to {trends?.date_range?.to}
            </Badge>
            <Badge variant="outline" className="flex items-center gap-1">
              <BarChart3 className="w-3 h-3" />
              {trends?.entries_count} entries analyzed
            </Badge>
          </div>
        </div>

        {/* Overview Section */}
        {trendsData?.overview && (
          <Card className="border-0 shadow-lg mb-6 bg-gradient-to-br from-purple-100 to-pink-100">
            <CardHeader>
              <CardTitle className="text-2xl text-purple-900">📊 Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg text-gray-800 leading-relaxed">{trendsData.overview}</p>
            </CardContent>
          </Card>
        )}

        {/* Three Main Trend Categories */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Physical Trends */}
          {trendsData?.physical_trends && (
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-blue-900">
                  <Activity className="w-5 h-5" />
                  Physical Performance
                </CardTitle>
                <CardDescription className="text-blue-800">
                  {trendsData.physical_trends.summary}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {trendsData.physical_trends.average_sleep !== undefined && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-blue-900">Average Sleep Quality</p>
                    <p className="text-2xl font-bold text-blue-700">
                      {trendsData.physical_trends.average_sleep}/10
                    </p>
                  </div>
                )}
                {trendsData.physical_trends.average_energy !== undefined && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-blue-900">Average Energy Level</p>
                    <p className="text-2xl font-bold text-blue-700">
                      {trendsData.physical_trends.average_energy}/10
                    </p>
                  </div>
                )}
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-700">Key Insights:</p>
                  {trendsData.physical_trends.insights?.map((insight, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-gray-700">{insight}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Mental Trends */}
          {trendsData?.mental_trends && (
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="bg-gradient-to-br from-purple-50 to-pink-100 rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-purple-900">
                  <Brain className="w-5 h-5" />
                  Mental & Emotional
                </CardTitle>
                <CardDescription className="text-purple-800">
                  {trendsData.mental_trends.summary}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {trendsData.mental_trends.average_mood !== undefined && (
                  <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                    <p className="text-sm font-medium text-purple-900">Average Mood</p>
                    <p className="text-2xl font-bold text-purple-700">
                      {trendsData.mental_trends.average_mood}/10
                    </p>
                  </div>
                )}
                {trendsData.mental_trends.average_confidence !== undefined && (
                  <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                    <p className="text-sm font-medium text-purple-900">Average Confidence</p>
                    <p className="text-2xl font-bold text-purple-700">
                      {trendsData.mental_trends.average_confidence}/10
                    </p>
                  </div>
                )}
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-700">Key Insights:</p>
                  {trendsData.mental_trends.insights?.map((insight, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-gray-700">{insight}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Performance Trends */}
          {trendsData?.performance_trends && (
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-green-900">
                  <Target className="w-5 h-5" />
                  Performance Patterns
                </CardTitle>
                <CardDescription className="text-green-800">
                  {trendsData.performance_trends.summary}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {trendsData.performance_trends.common_highlights && trendsData.performance_trends.common_highlights.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Common Highlights:</p>
                    <div className="space-y-1">
                      {trendsData.performance_trends.common_highlights.map((highlight, idx) => (
                        <Badge key={idx} className="bg-green-100 text-green-800 mr-2 mb-1">
                          {highlight}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-700">Key Insights:</p>
                  {trendsData.performance_trends.insights?.map((insight, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-gray-700">{insight}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Correlations */}
        {trendsData?.correlations && trendsData.correlations.length > 0 && (
          <Card className="border-0 shadow-lg mb-6">
            <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-100 rounded-t-lg">
              <CardTitle className="text-2xl text-orange-900">🔗 Key Correlations</CardTitle>
              <CardDescription className="text-orange-800">
                Discover how different factors influence your performance
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid md:grid-cols-2 gap-4">
                {trendsData.correlations.map((correlation, idx) => (
                  <div key={idx} className="p-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg border border-orange-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="text-xs">
                        {correlation.factor1?.replace(/_/g, ' ')}
                      </Badge>
                      <ArrowRight className="w-4 h-4 text-orange-600" />
                      <Badge variant="outline" className="text-xs">
                        {correlation.factor2?.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    <p className="font-semibold text-orange-900 mb-1">{correlation.relationship}</p>
                    <p className="text-sm text-gray-700">{correlation.evidence}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Strengths and Growth Areas */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Strengths */}
          {trendsData?.strengths && trendsData.strengths.length > 0 && (
            <Card className="border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-t-lg">
                <CardTitle className="text-xl text-green-900">💪 Your Strengths</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  {trendsData.strengths.map((strength, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-800">{strength}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Areas for Growth */}
          {trendsData?.areas_for_growth && trendsData.areas_for_growth.length > 0 && (
            <Card className="border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-t-lg">
                <CardTitle className="text-xl text-blue-900">🎯 Areas for Growth</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  {trendsData.areas_for_growth.map((area, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-800">{area}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Recommendations */}
        {trendsData?.recommendations && trendsData.recommendations.length > 0 && (
          <Card className="border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-br from-purple-50 to-pink-100 rounded-t-lg">
              <CardTitle className="text-2xl text-purple-900">
                <Lightbulb className="inline w-6 h-6 mr-2" />
                Personalized Recommendations
              </CardTitle>
              <CardDescription className="text-purple-800">
                Evidence-based actions to optimize your performance
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {trendsData.recommendations.map((rec, idx) => (
                  <div 
                    key={idx} 
                    className="p-4 bg-white rounded-lg border-l-4 border-purple-500 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex items-center gap-2">
                        {getCategoryIcon(rec.category)}
                        <span className="font-semibold text-gray-900 capitalize">
                          {rec.category}
                        </span>
                      </div>
                      <Badge className={getPriorityColor(rec.priority)}>
                        {rec.priority} priority
                      </Badge>
                    </div>
                    <p className="font-medium text-gray-800 mb-2">{rec.action}</p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Expected Impact:</span> {rec.expected_impact}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* CTA */}
        <div className="mt-8 text-center">
          <Button 
            onClick={() => navigate('/journal')} 
            size="lg"
            className="bg-purple-600 hover:bg-purple-700"
          >
            Continue Journaling
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-sm text-gray-600 mt-2">
            Keep tracking to get more accurate insights over time
          </p>
        </div>
      </div>
    </div>
  );
}
